import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Grid, 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  IconButton,
  CircularProgress,
  Divider
} from '@mui/material';
import { 
  Close as CloseIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

import { gallery as galleryApi, event as eventApi } from '../../../utils/api';
import { filestackClient, getTransformedImageUrl } from '../../../utils/filestack';

const EditGalleryModal = ({ open, onClose, onSuccess, gallery }) => {
  const { t } = useTranslation('gallery');
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [formData, setFormData] = useState({
    title: gallery.title || '',
    description: gallery.description || '',
    date: format(new Date(gallery.date), 'yyyy-MM-dd'),
    event: gallery.event?._id || '',
    images: gallery.images || []
  });
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [newImagesCount, setNewImagesCount] = useState(0);
  
  // Fetch events for dropdown
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await eventApi.getAll({ showPast: true });
        setEvents(data || []);
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };
    
    fetchEvents();
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const handleImageUpload = async () => {
    try {
      setUploading(true);
      
      // Use the initialized client
      await filestackClient.picker({
        accept: ['image/*'],
        maxFiles: 20,
        uploadInBackground: false,
        onUploadDone: (res) => {
          const newImages = res.filesUploaded.map((file, index) => {
            // Store the handle for transformation
            const handle = file.handle || file.url.split('/').pop();
            
            return {
              handle,
              url: getTransformedImageUrl(handle, 'gallery'),
              thumbnailUrl: getTransformedImageUrl(handle, 'thumbnail'),
              fullUrl: getTransformedImageUrl(handle, 'full'),
              originalUrl: file.url,
              caption: '',
              order: formData.images.length + index
            };
          });
          
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, ...newImages]
          }));
          
          setNewImagesCount(prev => prev + newImages.length);
          setUploading(false);
        },
        onClose: () => {
          setUploading(false);
        }
      }).open();
      
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload images');
      setUploading(false);
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = t('forms.titleRequired');
    }
    
    if (!formData.date) {
      newErrors.date = t('forms.dateRequired');
    }
    
    if (formData.images.length === 0) {
      newErrors.images = t('forms.imagesRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      // Prepare data for API
      const galleryData = {
        title: formData.title,
        description: formData.description,
        date: formData.date,
        event: formData.event || undefined, // Don't send empty string
        images: formData.images
      };
      
      await galleryApi.update(gallery._id, galleryData);
      
      toast.success(t('messages.updateSuccess'));
      onSuccess();
    } catch (error) {
      console.error('Error updating gallery:', error);
      toast.error(error.message || t('messages.updateError'));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={loading ? null : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {t('actions.edit')}
          <IconButton onClick={onClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <TextField
          name="title"
          label={t('forms.title')}
          placeholder={t('forms.titlePlaceholder')}
          fullWidth
          margin="normal"
          value={formData.title}
          onChange={handleChange}
          error={!!errors.title}
          helperText={errors.title}
          disabled={loading}
          required
        />
        
        <TextField
          name="description"
          label={t('forms.description')}
          placeholder={t('forms.descriptionPlaceholder')}
          fullWidth
          margin="normal"
          value={formData.description}
          onChange={handleChange}
          multiline
          rows={4}
          disabled={loading}
        />
        
        <TextField
          name="date"
          label={t('forms.date')}
          type="date"
          fullWidth
          margin="normal"
          value={formData.date}
          onChange={handleChange}
          error={!!errors.date}
          helperText={errors.date}
          disabled={loading}
          required
          InputLabelProps={{ shrink: true }}
        />
        
        <FormControl fullWidth margin="normal">
          <InputLabel id="event-select-label">{t('forms.event')}</InputLabel>
          <Select
            labelId="event-select-label"
            name="event"
            value={formData.event}
            onChange={handleChange}
            label={t('forms.event')}
            disabled={loading}
          >
            <MenuItem value="">
              <em>{t('forms.eventPlaceholder')}</em>
            </MenuItem>
            {events.map(event => (
              <MenuItem key={event._id} value={event._id}>
                {event.title} ({format(new Date(event.date), 'PP')})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('forms.images')}
            {errors.images && (
              <Typography color="error" variant="caption" sx={{ ml: 1 }}>
                {errors.images}
              </Typography>
            )}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('forms.editImagesNote', { count: gallery.images.length })}
          </Typography>
          
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleImageUpload}
            disabled={loading || uploading}
            fullWidth
          >
            {uploading ? (
              <CircularProgress size={24} />
            ) : (
              t('actions.addImages')
            )}
          </Button>
          
          {newImagesCount > 0 && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1, textAlign: 'center' }}>
              {newImagesCount} {newImagesCount === 1 ? t('gallery.newImageAdded') : t('gallery.newImagesAdded')}
            </Typography>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('actions.cancel')}
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : t('actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditGalleryModal; 